import { useEffect, useRef, useState } from "react";
import "../App.css";
import { Line, Province } from "../types/canvasTypes";

import {
  calculateAngle,
  calculateLineLength,
  connectLines,
  drawParallelLine,
  isLineValid,
  modifyEndPoint,
} from "../util/util";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useOptionContext } from "../context/OptionContext";
import { renderChange } from "../util/cost";

function Canvas() {
  function preventTouchScroll(e: TouchEvent) {
    e.preventDefault();
  }

  const {
    height,
    scale,
    lines,
    setLines,
    province,
    chalkLine,
    setChalkLine,
    gapSize,
    setGapSize,
  } = useOptionContext();

  const [currentLineLength, setCurrentLineLength] = useState<number>(0);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let globalLines = [...lines];
    let globalChalkLine = chalkLine;
    let globalGap = gapSize;
    let flipState = false;
    const editButton: HTMLElement | null =
      document.getElementById("editButton");

    const clearButton: HTMLElement | null =
      document.getElementById("clearButton");

    editButton?.addEventListener("click", editLast);
    clearButton?.addEventListener("click", () => {
      if (!canvas || !context) return;
      globalLines = [];
      renderChange(
        context,
        canvas,
        globalLines,
        height,
        scale,
        globalChalkLine,
        globalGap
      );

      setCurrentLineLength(0);
      setLines(globalLines);
    });
    const gapSlider: HTMLElement | null = document.getElementById("gapSlider");

    gapSlider?.addEventListener("change", (e) => {
      if (!canvas || !context) return;
      const inputElement = e.target as HTMLInputElement;
      globalGap = Number(inputElement.value || 1);

      setGapSize(globalGap);
      renderChange(
        context,
        canvas,
        globalLines,
        height,
        scale,
        globalChalkLine,
        globalGap
      );
    });

    const chalkLineButton: HTMLElement | null =
      document.getElementById("chalkLineButton");

    chalkLineButton?.addEventListener("click", chalkLinefunction);

    function chalkLinefunction() {
      if (!context || !canvas) {
        return;
      }

      globalChalkLine = !globalChalkLine;
      setChalkLine(globalChalkLine);

      renderChange(
        context,
        canvas,
        globalLines,
        height,
        scale,
        globalChalkLine,
        globalGap
      );

      setLines(globalLines);
    }

    const deleteButton: HTMLElement | null =
      document.getElementById("deleteButton");

    deleteButton?.addEventListener("click", deleteLast);

    const flipButton: HTMLElement | null =
      document.getElementById("flipButton");

    const addDoorButton: HTMLElement | null =
      document.getElementById("addDoorButton");

    addDoorButton?.addEventListener("click", () => {
      if (
        !globalLines ||
        !globalLines?.length ||
        globalLines?.length === 0 ||
        !canvas ||
        !context
      ) {
        return;
      }

      const line = globalLines[globalLines.length - 1];
      if (calculateLineLength(line, scale) < (height === 48 ? 56 : 72)) return;
      globalLines.pop();

      line.numberOfDoors = line.numberOfDoors === 1 ? 0 : 1;
      if (line.parallelLine) {
        line.parallelLine.numberOfDoors = line.numberOfDoors;
      }

      globalLines.push(line);

      renderChange(
        context,
        canvas,
        globalLines,
        height,
        scale,
        globalChalkLine,
        globalGap
      );

      setLines(globalLines);
    });
    const addDoubleDoorButton: HTMLElement | null = document.getElementById(
      "addDoubleDoorButton"
    );

    addDoubleDoorButton?.addEventListener("click", () => {
      if (
        !globalLines ||
        !globalLines?.length ||
        globalLines?.length === 0 ||
        !canvas ||
        !context
      ) {
        return;
      }

      const line = globalLines[globalLines.length - 1];
      if (calculateLineLength(line, scale) < 100) return;
      globalLines.pop();

      line.numberOfDoors = line.numberOfDoors === 2 ? 0 : 2;
      if (line.parallelLine) {
        line.parallelLine.numberOfDoors = line.numberOfDoors;
      }

      globalLines.push(line);

      renderChange(
        context,
        canvas,
        globalLines,
        height,
        scale,
        globalChalkLine,
        globalGap
      );

      setLines(globalLines);
    });

    flipButton?.addEventListener("click", () => {
      if (globalLines.length === 0 || !canvas || !context) {
        return;
      }

      const line = globalLines[globalLines.length - 1];
      globalLines.pop();
      flipState = !flipState;
      const newLine = drawParallelLine(line, flipState);
      globalLines.push(newLine);
      renderChange(
        context,
        canvas,
        globalLines,
        height,
        scale,
        globalChalkLine,
        globalGap
      );

      setLines(globalLines);
    });

    function deleteLast() {
      if (globalLines.length === 0) {
        return;
      }

      if (!context || !canvas) {
        return;
      }

      globalLines.pop();

      renderChange(
        context,
        canvas,
        globalLines,
        height,
        scale,
        globalChalkLine,
        globalGap
      );

      setLines(globalLines);
    }

    function editLast() {
      if (globalLines.length === 0 || !canvas || !context) return;

      var line = globalLines[globalLines.length - 1];
      globalLines.pop();
      var lineLength = calculateLineLength(line, scale);

      var newLength = prompt(
        "Line length: ".concat(
          lineLength.toString(),
          ". Please enter a new length:"
        )
      );

      if (newLength !== null && !isNaN(parseFloat(newLength))) {
        var scaleFactor = parseFloat(newLength) / lineLength;
        line.end.x = line.start.x + (line.end.x - line.start.x) * scaleFactor;
        line.end.y = line.start.y + (line.end.y - line.start.y) * scaleFactor;
        line = drawParallelLine(line, flipState);
        setCurrentLineLength(parseFloat(newLength));

        globalLines.push(line);
        renderChange(
          context,
          canvas,
          globalLines,
          height,
          scale,
          globalChalkLine,
          globalGap
        );

        setLines(globalLines);
      }
    }

    const canvas = canvasRef.current;
    if (canvas === null) return;
    const context = canvas.getContext("2d");
    let currentLine: Line | null = null;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const desiredWidth = Math.min(screenWidth * 0.9, 1000);
    const desiredHeight = Math.min(screenHeight * 0.7, 1000);
    // Set the canvas width and height
    canvas.width = desiredWidth;
    canvas.height = desiredHeight;

    if (!context) return;

    renderChange(
      context,
      canvas,
      globalLines,
      height,
      scale,
      globalChalkLine,
      globalGap
    );

    let isDrawing = false;

    function startDrawing(event: MouseEvent | TouchEvent) {
      if (!context) return;
      let startX = 0;
      let startY = 0;

      if (event instanceof MouseEvent) {
        startX = event.offsetX;
        startY = event.offsetY;
      } else if (event instanceof TouchEvent) {
        document.addEventListener("touchmove", preventTouchScroll, {
          passive: false,
        });

        const touch = event.touches[0];
        const targetElement = event.target as Element;
        const rect = targetElement.getBoundingClientRect();

        startX = touch.clientX - rect.left;
        startY = touch.clientY - rect.top;
      }

      if (globalLines.length > 0) {
        const start = connectLines({ x: startX, y: startY }, globalLines);
        startX = start.x;
        startY = start.y;
      }

      currentLine = {
        start: { x: startX, y: startY },
        end: { x: startX, y: startY },
        numberOfDoors: 0,
      };

      isDrawing = true;
    }

    function drawLine(e: MouseEvent | TouchEvent) {
      if (!isDrawing || !canvas || !context) return;

      let mouseX = 0;
      let mouseY = 0;

      if (e instanceof MouseEvent) {
        mouseX = e.offsetX;
        mouseY = e.offsetY;
      } else if (e instanceof TouchEvent) {
        const touch = e.touches[0];
        const targetElement = e.target as Element;
        const rect = targetElement.getBoundingClientRect();

        mouseX = touch.clientX - rect.left;
        mouseY = touch.clientY - rect.top;
      }

      if (currentLine) {
        currentLine.end = { x: mouseX, y: mouseY };
      }

      context.clearRect(0, 0, canvas.width, canvas.height);

      globalLines.forEach((line) => {
        context.beginPath();
        context.moveTo(line.start.x, line.start.y);
        context.lineTo(line.end.x, line.end.y);
        context.stroke();
      });

      if (currentLine) {
        context.beginPath();
        context.moveTo(currentLine.start.x, currentLine.start.y);
        context.lineTo(currentLine.end.x, currentLine.end.y);
        context.stroke();
        const length = calculateLineLength(currentLine, scale);
        setCurrentLineLength(length);
      }
    }

    function finishDrawing(e: MouseEvent | TouchEvent) {
      if (!context || !canvas) return;

      if (isDrawing) {
        let endX = 0;
        let endY = 0;

        if (e instanceof MouseEvent) {
          endX = e.offsetX;
          endY = e.offsetY;
        } else if (e instanceof TouchEvent) {
          if (e.changedTouches.length > 0) {
            const touch = e.changedTouches[e.changedTouches.length - 1];
            const targetElement = e.target as HTMLElement | null;

            if (!targetElement) return;

            const rect = targetElement.getBoundingClientRect();

            endX = touch.clientX - rect.left;
            endY = touch.clientY - rect.top;
          } else {
            return;
          }
        }

        if (currentLine) {
          if (!isLineValid(currentLine)) {
            currentLine = null;
            renderChange(
              context,
              canvas,
              globalLines,
              height,
              scale,
              globalChalkLine,
              globalGap
            );
            return;
          }
          currentLine.end = { x: endX, y: endY };
          currentLine.angle = calculateAngle(currentLine);
          currentLine = modifyEndPoint(currentLine);
          currentLine = drawParallelLine(currentLine, flipState);
          globalLines.push(currentLine);
          currentLine = null;

          renderChange(
            context,
            canvas,
            globalLines,
            height,
            scale,
            globalChalkLine,
            globalGap
          );

          setLines(globalLines);
        }
      }

      document.removeEventListener("touchmove", preventTouchScroll);
      isDrawing = false;
    }

    canvas.addEventListener("touchstart", startDrawing);
    canvas.addEventListener("touchmove", drawLine);
    canvas.addEventListener("touchend", finishDrawing);
    canvas.addEventListener("touchcancel", finishDrawing);

    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", drawLine);
    canvas.addEventListener("mouseup", finishDrawing);
    canvas.addEventListener("mouseout", finishDrawing);

    // Clean up event listeners
    return () => {
      canvas.addEventListener("touchstart", startDrawing);
      canvas.addEventListener("touchmove", drawLine);
      canvas.addEventListener("touchend", finishDrawing);
      canvas.addEventListener("touchcancel", finishDrawing);

      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", drawLine);
      canvas.removeEventListener("mouseup", finishDrawing);
      canvas.removeEventListener("mouseout", finishDrawing);
    };
  }, []);

  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    const today = new Date();
    today.toLocaleDateString("fr-FR");
    setCurrentDate(today.toLocaleDateString("fr-FR"));
  }, []);

  return (
    <div className="page">
      <div style={{ width: "100%", textAlign: "left" }} className="title">
        <h1>
          {province === Province.Ontario ? "Second step" : "Deuxième étape"}
        </h1>
        {province === Province.Quebec && (
          <div
            style={{
              padding: "20px",
              fontFamily: "Arial, sans-serif",
              lineHeight: "1.6",
            }}
          >
            <h2>Instructions :</h2>
            <ol>
              <li>
                <strong>Tracer des lignes :</strong> Utilisez votre souris pour
                tracer des lignes sur la toile. Il suffit de{" "}
                <strong>cliquer, maintenir et glisser</strong> pour dessiner une
                ligne.
              </li>
              <li>
                Relâchez le bouton de la souris pour terminer votre ligne.
              </li>
              <li>
                Lorsque vous êtes prêt,{" "}
                <strong>faites défiler vers le bas</strong> pour passer à{" "}
                <strong>l'Étape 3</strong> et continuer le processus.
              </li>
            </ol>
          </div>
        )}
        <p>{currentDate}</p>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>

      <div
        style={{
          display: "flex",
          alignContent: "space-around",
          justifyContent: "space-around",
          width: "100%",
        }}
      >
        <div>
          <div className="hide-on-print"></div>
          <div
            className="hide-on-print"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignContent: "center",
              flexWrap: "wrap",
              maxWidth: "100%",
            }}
          >
            <h3>
              {province === Province.Ontario ? "Length" : "Longueur"}:{" "}
              <span id="currentLineLength" style={{ color: "red" }}>
                {currentLineLength.toFixed(2)}
              </span>{" "}
              ''
            </h3>
            <div
              style={{
                alignSelf: "center",
                gap: "20px",
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              <button
                style={{ alignSelf: "center" }}
                id="editButton"
                className="edit-button"
              >
                {province === Province.Ontario ? "edit" : "modifier"}
              </button>
              <button
                style={{ alignSelf: "center" }}
                id="flipButton"
                className="edit-button"
              >
                {" "}
                {province === Province.Ontario
                  ? "reverse the side"
                  : "inverser le côté"}
              </button>
              {height === 48 && (
                <button
                  onClick={() =>
                    calculateLineLength(lines[lines.length - 1], scale) > 100
                      ? toast.info("Ajouté deux portes 🚪!")
                      : toast.error(
                          province === Province.Ontario
                            ? "Error: the side is smaller than 100''"
                            : "Erreur : le côté est plus petit que 100 pouces"
                        )
                  }
                  style={{ alignSelf: "center" }}
                  id="addDoubleDoorButton"
                  className="edit-button"
                >
                  {province === Province.Ontario
                    ? "add two door  🚪🚪"
                    : "2 portes 🚪🚪"}
                </button>
              )}
              <button
                onClick={() =>
                  calculateLineLength(lines[lines.length - 1], scale) >
                  (height === 48 ? 56 : 72)
                    ? toast.info("Ajouté une porte 🚪!")
                    : toast.error(
                        province === Province.Ontario
                          ? `Error: the side is smaller than ${
                              height === 48 ? 56 : 72
                            }''`
                          : `Erreur : le côté est plus petit que ${
                              height === 48 ? 56 : 72
                            } pouces`
                      )
                }
                style={{ alignSelf: "center" }}
                id="addDoorButton"
                className="edit-button"
              >
                {province === Province.Ontario ? "add door 🚪" : "porte 🚪"}
              </button>

              <button
                style={{ alignSelf: "center" }}
                id="deleteButton"
                className="delete-button"
              >
                {province === Province.Ontario
                  ? "delete last line"
                  : "supprimer le côté"}
              </button>
            </div>
          </div>

          <canvas
            className="canvas-container"
            ref={canvasRef}
            id="canvas"
            height="600px"
            width="600px"
            style={{
              backgroundColor: "antiquewhite",
              margin: "20px auto",
              justifySelf: "center",
            }}
          ></canvas>
          <div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  style={{ alignSelf: "center" }}
                  id="clearButton"
                  className="delete-button"
                >
                  {province === Province.Ontario
                    ? "reset the board"
                    : "effacer le tableau"}
                </button>

                <div
                  style={{
                    width: "100px",
                    display: "flex",
                    visibility: chalkLine ? "visible" : "hidden",
                  }}
                >
                  <label>
                    {province === Province.Ontario
                      ? "Gap Width"
                      : "Largeur de ligne"}
                    :
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.05"
                      id="gapSlider"
                      style={{ marginLeft: "5px" }}
                    />
                  </label>
                  {gapSize}
                </div>

                <label>
                  <input
                    type="checkbox"
                    id="chalkLineButton"
                    checked={chalkLine}
                    // onChange={handleCheckboxChange}
                  />
                  {province === Province.Ontario
                    ? "chalk line measurment"
                    : "chalk line"}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Canvas;
